/**
 * ContactDetailSheet — Admin ve Agent için ORTAK kontakt detay görünümü.
 *
 * Sekmeler:
 *   Info         — iletişim + Rechnungs-/Lieferanschrift + zu pflegende Daten
 *   Umsatz       — dönem filtreli toplam ciro + grafik (UmsatzPanel)
 *   Bestellungen — tüm sipariş geçmişi
 *   Preise       — kayıtlı müşteri fiyatları (CustomerPriceManager)
 *   To-dos       — kontakta bağlı görevler
 *   Zugang       — Kundenportal-Zugang per Einladungslink (ZugangPanel)
 */
import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  AlertTriangle, Mail, Phone, MapPin, User, Pencil,
  CheckCircle2, Circle, Plus,
} from 'lucide-react';
import { toast } from 'sonner';
import CustomerPriceManager from '@/components/products/CustomerPriceManager';
import UmsatzPanel from '@/components/contacts/UmsatzPanel';
import ZugangPanel from '@/components/contacts/ZugangPanel';
import { getKundentypBadgeClass } from '@/lib/kundentyp';
import { getMissingData } from '@/lib/contactUtils';

export default function ContactDetailSheet({ lead: leadProp, open, onOpenChange, onEdit, currentUserEmail }) {
  const queryClient = useQueryClient();
  const [newTodo, setNewTodo] = useState('');

  // Taze kontakt verisi — login üretimi vb. sonrası anında güncellenir
  const { data: freshLead } = useQuery({
    queryKey: ['lead-detail', leadProp?.id],
    queryFn: () => base44.entities.Lead.get(leadProp.id),
    enabled: !!leadProp?.id && open,
  });
  const lead = freshLead || leadProp;

  // Kontakta bağlı siparişler (ciro + geçmiş)
  const { data: orders = [] } = useQuery({
    queryKey: ['lead-orders', leadProp?.id],
    queryFn: () => base44.entities.Order.filter({ lead_id: leadProp?.id }, '-created_date'),
    enabled: !!leadProp?.id && open,
  });

  // Kontakta bağlı görevler
  const { data: todos = [] } = useQuery({
    queryKey: ['lead-tasks', leadProp?.id],
    queryFn: () => base44.entities.Task.filter({ lead_id: leadProp?.id }, '-created_date'),
    enabled: !!leadProp?.id && open,
  });

  const updateLead = useMutation({
    mutationFn: (data) => base44.entities.Lead.update(lead.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead-detail', leadProp?.id] });
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['admin-leads'] });
      queryClient.invalidateQueries({ queryKey: ['agent-leads'] });
    },
    onError: (err) => toast.error('Fehler: ' + err.message),
  });

  const addTodo = useMutation({
    mutationFn: (beschreibung) =>
      base44.entities.Task.create({
        lead_id: lead.id,
        lead_name: lead.name,
        typ: 'To-do',
        beschreibung,
        status: 'Offen',
        datum: new Date().toISOString().slice(0, 10),
        assigned_agent: currentUserEmail || null,
      }),
    onSuccess: () => {
      setNewTodo('');
      queryClient.invalidateQueries({ queryKey: ['lead-tasks', leadProp?.id] });
    },
    onError: (err) => toast.error('Fehler: ' + err.message),
  });

  const toggleTodo = useMutation({
    mutationFn: async (task) => {
      const completing = task.status !== 'Erledigt';
      await base44.entities.Task.update(task.id, {
        status: completing ? 'Erledigt' : 'Offen',
      });
      // Vertriebsprozess Schritt 1: Erstbesuch abgeschlossen →
      // Kontakt automatisch auf "Interessenten".
      const typ = String(task.task_type || task.typ || '').toLowerCase();
      if (completing && typ === 'erstbesuch') {
        await base44.entities.Lead.update(lead.id, { status: 'Interessenten' });
        toast.success('Erstbesuch abgeschlossen — Status: „Interessenten“');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead-tasks', leadProp?.id] });
      queryClient.invalidateQueries({ queryKey: ['lead-detail', leadProp?.id] });
      queryClient.invalidateQueries({ queryKey: ['admin-leads'] });
      queryClient.invalidateQueries({ queryKey: ['agent-leads'] });
    },
  });

  // Ciro/sipariş sayısı yalnızca gerçekleşen siparişlerden (Bestellung) —
  // teklifler (Angebot) ve iptaller (Storniert) hariç tutulur.
  const validOrders = useMemo(
    () => orders.filter((o) => o.status !== 'Storniert' && o.order_type !== 'Angebot'),
    [orders]
  );
  const lifetimeRevenue = validOrders.reduce(
    (s, o) => s + parseFloat(o.total ?? o.gesamtsumme ?? 0), 0
  );

  if (!lead) return null;
  const missing = getMissingData(lead);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader className="mb-2">
          <SheetTitle className="flex items-center gap-2 flex-wrap">
            {lead.name}
            <Badge variant="outline" className={getKundentypBadgeClass(lead.kundentyp)}>
              {lead.kundentyp || 'B2B'}
            </Badge>
            {!lead.email && (
              <span title="E-Mail fehlt!">
                <AlertTriangle className="w-5 h-5 text-red-500" />
              </span>
            )}
          </SheetTitle>
        </SheetHeader>

        <div className="flex items-center justify-between mb-3">
          <div className="text-xs text-slate-500">
            Gesamtumsatz: <span className="font-bold text-slate-900">
              € {lifetimeRevenue.toLocaleString('de-DE', { minimumFractionDigits: 2 })}
            </span> · {validOrders.length} Bestellungen
          </div>
          {onEdit && (
            <Button size="sm" variant="outline" onClick={() => onEdit(lead)}>
              <Pencil className="w-3.5 h-3.5 mr-1" /> Bearbeiten
            </Button>
          )}
        </div>

        <Tabs defaultValue="uebersicht">
          <TabsList className="grid grid-cols-6 w-full">
            <TabsTrigger value="uebersicht" className="text-xs">Info</TabsTrigger>
            <TabsTrigger value="umsatz" className="text-xs">Umsatz</TabsTrigger>
            <TabsTrigger value="bestellungen" className="text-xs">
              Bestell. ({orders.length})
            </TabsTrigger>
            <TabsTrigger value="preise" className="text-xs">Preise</TabsTrigger>
            <TabsTrigger value="todos" className="text-xs">
              To-dos ({todos.filter((t) => t.status !== 'Erledigt').length})
            </TabsTrigger>
            <TabsTrigger value="zugang" className="text-xs">Zugang</TabsTrigger>
          </TabsList>

          {/* ── Info ── */}
          <TabsContent value="uebersicht" className="space-y-4 mt-4">
            {missing.length > 0 && (
              <Card className="border-red-200 bg-red-50">
                <CardContent className="p-4">
                  <h4 className="text-sm font-bold text-red-700 flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-4 h-4" /> Zu pflegende Daten
                  </h4>
                  <ul className="space-y-1">
                    {missing.map((m) => (
                      <li key={m.field} className="text-xs text-red-700 flex items-center gap-2">
                        {m.critical ? '🔴' : '🟡'} {m.label}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardContent className="p-4 space-y-2 text-sm">
                <h4 className="text-xs font-bold text-slate-400 uppercase mb-1">Kontakt</h4>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-slate-400" />
                  {lead.email
                    ? <a className="text-blue-600 hover:underline" href={`mailto:${lead.email}`}>{lead.email}</a>
                    : <span className="text-red-600 font-medium flex items-center gap-1">
                        <AlertTriangle className="w-4 h-4" /> E-Mail fehlt — bitte pflegen!
                      </span>}
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-slate-400" />
                  {lead.telefon || <span className="text-slate-400">—</span>}
                </div>
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-slate-400" />
                  {lead.ansprechpartner || <span className="text-slate-400">Kein Ansprechpartner</span>}
                </div>
              </CardContent>
            </Card>

            {/* Zahlungsbedingungen */}
            <Card>
              <CardContent className="p-4">
                <h4 className="text-xs font-bold text-slate-400 uppercase mb-2">Zahlungsbedingungen</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase">Skonto</p>
                    <p className="font-semibold">{Number(lead.skonto_prozent) || 0} %</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase">Skonto Tage</p>
                    <p className="font-semibold">{Number(lead.skonto_tage) || 0} Tage</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase">Kundenrabatt</p>
                    <p className="font-semibold">{Number(lead.kundenrabatt_prozent) || 0} %</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase">Zahlungsziel</p>
                    <p className="font-semibold">{lead.zahlungsziel_tage ?? 5} Tage</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Card>
                <CardContent className="p-4 text-sm">
                  <h4 className="text-xs font-bold text-slate-400 uppercase mb-2 flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5" /> Rechnungsanschrift
                  </h4>
                  <p>{lead.strasse || '—'}</p>
                  <p>{[lead.plz, lead.ort].filter(Boolean).join(' ') || ''}</p>
                  <p className="text-slate-500">{lead.land || 'Deutschland'}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-sm">
                  <h4 className="text-xs font-bold text-slate-400 uppercase mb-2 flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5" /> Lieferanschrift
                  </h4>
                  {lead.liefer_abweichend ? (
                    <>
                      <p>{lead.liefer_strasse || '—'}</p>
                      <p>{[lead.liefer_plz, lead.liefer_ort].filter(Boolean).join(' ')}</p>
                      <p className="text-slate-500">{lead.liefer_land || 'Deutschland'}</p>
                    </>
                  ) : (
                    <p className="text-slate-400 italic">= Rechnungsanschrift</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ── Umsatz ── */}
          <TabsContent value="umsatz" className="mt-4">
            <UmsatzPanel orders={orders} />
          </TabsContent>

          {/* ── Bestellungen ── */}
          <TabsContent value="bestellungen" className="space-y-2 mt-4">
            {orders.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-8">Noch keine Bestellungen</p>
            ) : (
              orders.map((o) => (
                <Card key={o.id}>
                  <CardContent className="p-3 flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-bold text-slate-900">
                        {o.order_number || o.id.slice(0, 8)}
                        <Badge variant="outline" className="ml-2 text-[9px] uppercase">
                          {o.source || 'agent'}
                        </Badge>
                      </p>
                      <p className="text-xs text-slate-500">
                        {new Date(o.created_date).toLocaleDateString('de-DE')} · {o.status}
                      </p>
                    </div>
                    <p className="text-sm font-black text-slate-900">
                      € {parseFloat(o.total ?? o.gesamtsumme ?? 0).toLocaleString('de-DE', { minimumFractionDigits: 2 })}
                    </p>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          {/* ── Preise ── */}
          <TabsContent value="preise" className="mt-4">
            <CustomerPriceManager leadId={lead.id} leadName={lead.name} />
          </TabsContent>

          {/* ── To-dos ── */}
          <TabsContent value="todos" className="space-y-3 mt-4">
            <div className="flex gap-2">
              <Input
                placeholder="Neues To-do für diesen Kontakt…"
                value={newTodo}
                onChange={(e) => setNewTodo(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && newTodo.trim() && addTodo.mutate(newTodo.trim())}
              />
              <Button
                size="sm"
                disabled={!newTodo.trim() || addTodo.isPending}
                onClick={() => addTodo.mutate(newTodo.trim())}
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {todos.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-6">Keine To-dos</p>
            ) : (
              todos.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => toggleTodo.mutate(t)}
                  className="w-full flex items-start gap-2 p-3 bg-white border rounded-lg text-left hover:bg-slate-50"
                >
                  {t.status === 'Erledigt'
                    ? <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                    : <Circle className="w-4 h-4 text-slate-300 mt-0.5 shrink-0" />}
                  <div>
                    <p className={`text-sm ${t.status === 'Erledigt' ? 'line-through text-slate-400' : 'text-slate-900'}`}>
                      {t.beschreibung || t.typ}
                    </p>
                    <p className="text-[10px] text-slate-400">
                      {t.datum && new Date(t.datum).toLocaleDateString('de-DE')} · {t.status}
                    </p>
                  </div>
                </button>
              ))
            )}
          </TabsContent>

          {/* ── Zugang ── */}
          <TabsContent value="zugang" className="mt-4">
            <ZugangPanel lead={lead} />
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
